/**
 * Приведение источников данных к единой модели карточки команды (KAN-814).
 *
 * Правило одно на всех: пустое поле не превращается в прочерк, пустой бейдж или
 * серый квадрат — оно просто не попадает в модель (см. KAN-807). Свободный слот
 * это «?» и роль, больше про него сказать нечего.
 */

import type { RosterListItem } from "@/shared/api/roster";
import { getBannerUrl } from "@/shared/lib/image-utils";
import { getOpggUrl } from "@/shared/lib/opgg";
import { normalizeLolRole } from "@/shared/ui/data-display/role-paths";

/** Ключ роли для данных карточки; переводит его место рендера (KAN-1267). */
const roleKey = (role: string): string => normalizeLolRole(role) ?? role;

import {
  ROSTER_LANES,
  type RosterCardData,
  type RosterLane,
  type RosterLaneSlot,
  type RosterPersonView,
} from "./types";

/** Заглушки, которыми бэкенд и старый фронт заполняли отсутствие значения. */
const PLACEHOLDER_LABELS = new Set(["—", "-", "–", "?", "n/a", "null", "undefined"]);

/** Оставляет только те подписи, за которыми стоят данные. */
export function cleanLabels(labels?: readonly (string | null | undefined)[]): string[] {
  return (labels ?? [])
    .map((l) => String(l ?? "").trim())
    .filter((l) => l !== "" && !PLACEHOLDER_LABELS.has(l.toLowerCase()));
}

const isLane = (key: string | null): key is RosterLane =>
  key !== null && (ROSTER_LANES as readonly string[]).includes(key);

/** Пустой костяк из пяти линий — на нём собираются составы из любого источника. */
function emptyLanes(): RosterLaneSlot[] {
  return ROSTER_LANES.map((lane) => ({ lane }));
}

/**
 * Участник команды в том виде, в каком он приходит из разных мест: у профиля
 * плоские алиасы (`name`/`avatar`), у списка команд — `displayName`/`avatarUrl`.
 * Тип структурный намеренно: `shared` не должен знать про `entities`.
 */
export interface RosterMemberLike {
  id?: string | null;
  userId?: string | null;
  username?: string | null;
  name?: string | null;
  displayName?: string | null;
  avatar?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
  rank?: string | null;
  riotRank?: string | null;
  region?: string | null;
  riotGameName?: string | null;
  riotTagLine?: string | null;
  riotRegion?: string | null;
  riotProfileIconId?: number | null;
}

/** Команда в том виде, в каком её отдают профиль и публичная страница игрока. */
export interface RosterTeamLike {
  id?: string | null;
  name?: string | null;
  tag?: string | null;
  icon?: string | null;
  logoUrl?: string | null;
  banner?: string | null;
  isVerified?: boolean | null;
  isLookingForPartner?: boolean | null;
  isLookingForPlayers?: boolean | null;
  averageRank?: string | null;
  members?: RosterMemberLike[] | null;
}

function memberName(member: RosterMemberLike): string {
  return String(
    member.displayName ?? member.name ?? member.username ?? member.riotGameName ?? "",
  ).trim();
}

function personFrom(member: RosterMemberLike): RosterPersonView | null {
  const displayName = memberName(member);
  // Профиль подставляет участников-плейсхолдеров с пустыми полями, пока команда
  // не догрузилась. Безымянный участник — это не участник.
  if (!displayName) return null;

  const role = String(member.role ?? "").trim();
  const opggUrl = getOpggUrl(
    member.riotGameName ?? undefined,
    member.riotTagLine ?? undefined,
    member.riotRegion ?? member.region ?? undefined,
  );

  return {
    id: member.id ?? member.userId ?? null,
    displayName,
    avatarUrl: member.avatarUrl ?? member.avatar ?? null,
    riotProfileIconId: member.riotProfileIconId ?? null,
    roleLabel: role ? roleKey(role) : null,
    opggUrl,
    username: member.username ?? null,
  };
}

/** Подписи слота: роль и ранг, если они известны. */
function badgesFor(member: RosterMemberLike, lane: RosterLane): string[] {
  const role = String(member.role ?? "").trim();
  return cleanLabels([role ? roleKey(role) : roleKey(lane), member.riotRank ?? member.rank]);
}

/**
 * Раскладывает участников по линиям. Кто не встал на линию (запас, тренер,
 * команда без ролей, второй игрок на ту же линию) — уходит в `extras`, а не
 * теряется.
 */
export function lanesFromMembers(members: readonly RosterMemberLike[]): {
  lanes: RosterLaneSlot[];
  extras: RosterPersonView[];
} {
  const lanes = emptyLanes();
  const byLane = new Map<RosterLane, RosterLaneSlot>(lanes.map((s) => [s.lane, s]));
  const extras: RosterPersonView[] = [];

  for (const member of members) {
    const person = personFrom(member);
    if (!person) continue;

    const lane = normalizeLolRole(member.role ?? "");
    const slot = isLane(lane) ? byLane.get(lane) : undefined;
    if (slot && !slot.member) {
      slot.member = person;
      slot.badges = badgesFor(member, slot.lane);
    } else {
      extras.push(person);
    }
  }

  for (const slot of lanes) {
    if (!slot.member) slot.badges = [roleKey(slot.lane)];
  }

  return { lanes, extras };
}

/** Плитка каталога `/rosters`. */
export function rosterListItemToCard(item: RosterListItem): RosterCardData {
  const { lanes, extras } = lanesFromMembers(item.members ?? []);
  return {
    id: item.id,
    name: item.name,
    tag: item.tag,
    logoUrl: item.logoUrl ?? item.icon ?? null,
    bannerUrl: item.banner ? getBannerUrl(item.banner) : null,
    isVerified: Boolean(item.isVerified),
    isLookingForPlayers: Boolean(item.isLookingForPlayers),
    averageRank: item.averageRank ?? null,
    memberCount: item.memberCount,
    href: `/rosters/${item.id}`,
    lanes,
    extras,
  };
}

/** Команда из профиля и публичной страницы игрока. */
export function teamToCard(
  team: RosterTeamLike,
  options: { href?: string | null } = {},
): RosterCardData {
  const { lanes, extras } = lanesFromMembers(team.members ?? []);
  const id = String(team.id ?? "");
  const realMembers = lanes.filter((s) => s.member).length + extras.length;

  return {
    id,
    name: String(team.name ?? ""),
    tag: team.tag ?? null,
    logoUrl: team.logoUrl ?? team.icon ?? null,
    bannerUrl: null,
    isVerified: Boolean(team.isVerified),
    isLookingForPlayers: Boolean(team.isLookingForPlayers ?? team.isLookingForPartner),
    averageRank: team.averageRank ?? null,
    memberCount: realMembers,
    href: options.href !== undefined ? options.href : id ? `/rosters/${id}` : null,
    lanes,
    extras,
  };
}
